import * as React from 'react';
import {
    IOptionModifierSettings,
    IProduct,
    IProductModifierSettings,
    isProductAvailable
} from './model/Menu';
import { useCalories } from 'src/hooks/useCalories';
import { useCurrencyString } from 'lib/useCurrencyString';
import { useTranslation } from 'react-i18next';
import { QuantityFormField, QuantityLabel } from 'lib/form/QuantityFormField';
import { Box, styled } from '@mui/material';
import { isDefined, isEmptyString, isNumber, isString } from 'lib/typeInference';
import { ModifierSelectFormField } from './modifierSelect/ModifierSelectFormField';

const PREFIX = 'ProductModifierSelect';
const classes = {
    modifierSelect: `${PREFIX}modifierSelect`
};

const StyledBox = styled(Box)(({ theme }) => ({
    marginTop: theme.spacing(1),
    color: theme.palette.text.primary,
    [`& .${classes.modifierSelect}`]: {
        marginTop: theme.spacing(1),
        color: theme.palette.text.primary
    }
}));

interface IProductModifierSelectProps {
    modifier: IProductModifierSettings;
    index: number;
    formProductModifiers: (string[] | '')[];
    allProductModifiers: IProductModifierSettings[];
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
}
export function ProductModifierSelect({
    modifier,
    index,
    formProductModifiers,
    allProductModifiers,
    setFieldValue
}: IProductModifierSelectProps) {
    const { getCaloriesByNutrition, getCaloriesString } = useCalories();
    const getCurrencyString = useCurrencyString();
    const { t } = useTranslation();

    const getProductModifierOptions = React.useCallback(
        (modifierProduct: IProduct) => {
            const details = {
                label: `${modifierProduct.title} +${getCurrencyString(modifierProduct.price)}`,
                value: modifierProduct.id,
                selected: modifierProduct.selected,
                description: ''
            };
            if (modifierProduct.nutrition && modifierProduct.nutrition.length > 0) {
                const modifierCalories = Number(getCaloriesByNutrition(modifierProduct.nutrition));
                if (modifierCalories >= 0) {
                    const caloriesString = `${getCaloriesString(modifierCalories)} ${t(
                        'ORDER_CALORIES_PER_UNIT'
                    )}`;
                    details.description +=
                        details.description.length > 0 ? ` | ${caloriesString}` : caloriesString;
                }
            }
            return details;
        },
        [getCaloriesByNutrition, getCaloriesString, getCurrencyString, t]
    );

    const showPrice = React.useCallback(
        (price: number) => {
            if (isNumber(price) && price > 0) {
                return `+${getCurrencyString(price)}`;
            }
            return '';
        },
        [getCurrencyString]
    );

    const getProductQuantityModifierOptions = React.useCallback(
        (isBundle?: boolean) => (acc: Record<string, QuantityLabel>, modifierProduct: IProduct) => {
            acc[modifierProduct.id] = {
                title: modifierProduct.title,
                description: isBundle ? undefined : showPrice(modifierProduct.price),
                image:
                    modifierProduct?.modifierImage ||
                    modifierProduct.listingImageUrl ||
                    modifierProduct.imageUrl,
                disabled: !isProductAvailable(modifierProduct),
                sort: modifierProduct.sort
            };
            if (modifierProduct.nutrition && modifierProduct.nutrition.length > 0) {
                const modifierCalories = getCaloriesByNutrition(modifierProduct.nutrition);

                if (modifierCalories && Number(modifierCalories) >= 0) {
                    const caloriesString = `${getCaloriesString(Number(modifierCalories))}  ${t(
                        'ORDER_CALORIES_PER_UNIT'
                    )}`;
                    if (
                        !isDefined(acc[modifierProduct.id].description) ||
                        isEmptyString(acc[modifierProduct.id].description)
                    ) {
                        acc[modifierProduct.id].description = caloriesString;
                    } else if (isString(acc[modifierProduct.id].description)) {
                        acc[modifierProduct.id].description += ` | ${caloriesString}`;
                    }
                }
            }
            return acc;
        },
        [getCaloriesByNutrition, getCaloriesString, showPrice, t]
    );

    const renderModifierSelectionInfo = React.useCallback(
        (min: number, max: number) => {
            if (min === 1 && max === min) {
                return t('MENU_MODIFIERS_CUSTOMIZATIONS_REQUIRED_SINGLE');
            }
            if (min >= 1) {
                if (max === min) {
                    return t('MENU_MODIFIERS_CUSTOMIZATIONS_REQUIRED_MULTIPLE', { min });
                }
                return t('MENU_MODIFIERS_CUSTOMIZATIONS_REQUIRED_MULTIPLEDIFFERENCE', { min, max });
            }
        },
        [t]
    );

    const renderProductModifierOptions = React.useCallback(
        (isBundle?: boolean) => (modifierProduct: IProduct) => {
            const details = {
                label: modifierProduct.title,
                price: isBundle ? undefined : `+${getCurrencyString(modifierProduct.price)}`,
                value: modifierProduct.id,
                disabled: !isProductAvailable(modifierProduct),
                image:
                    modifierProduct?.modifierImage ||
                    modifierProduct.listingImageUrl ||
                    modifierProduct.imageUrl,
                description: isBundle ? '' : showPrice(modifierProduct.price)
            };
            if (modifierProduct.nutrition && modifierProduct.nutrition.length > 0) {
                const modifierCalories = getCaloriesByNutrition(modifierProduct.nutrition);
                if (modifierCalories && Number(modifierCalories) >= 0) {
                    const caloriesString = `${getCaloriesString(Number(modifierCalories))}  ${t(
                        'ORDER_CALORIES_PER_UNIT'
                    )}`;
                    details.description +=
                        details.description.length > 0 ? ` | ${caloriesString}` : caloriesString;
                }
            }

            return details;
        },
        [getCaloriesByNutrition, getCaloriesString, getCurrencyString, showPrice, t]
    );

    const modifierValidation = React.useCallback(
        (modifier: IProductModifierSettings | IOptionModifierSettings) => (modifiers: string[]) => {
            let error;
            // If only one modifier should be selected component returns string
            // instead of one item inside array.
            const flattenModifiers = [modifiers].flat();
            if (modifier.min !== 0) {
                if (
                    !modifiers ||
                    flattenModifiers.length < modifier.min ||
                    flattenModifiers.length > modifier.max
                ) {
                    error = renderModifierSelectionInfo(modifier.min, modifier.max);
                }
            }
            return error;
        },
        [renderModifierSelectionInfo]
    );

    const products = React.useMemo(
        () => modifier.products.map(getProductModifierOptions),
        [modifier.products, getProductModifierOptions]
    );
    const selectedIds = React.useMemo(
        () => products.filter(product => product.selected).map(product => product.value),
        [products]
    );

    const showModifier = React.useMemo(() => {
        if (modifier.bundle && modifier.products.length === 1 && selectedIds.length) {
            return false;
        }
        if (modifier.parentModifierId && modifier.parentProductId) {
            const parentModifierIndex = allProductModifiers.findIndex(
                m => m.id === modifier.parentModifierId
            );
            if (parentModifierIndex !== -1) {
                const isProductSelected = Array.isArray(formProductModifiers[parentModifierIndex])
                    ? formProductModifiers[parentModifierIndex].includes(modifier.parentProductId)
                    : formProductModifiers[parentModifierIndex] === modifier.parentProductId;
                if (!isProductSelected) {
                    return false;
                }
            }
        }
        return true;
    }, [
        modifier.bundle,
        modifier.products,
        modifier.parentModifierId,
        modifier.parentProductId,
        formProductModifiers,
        allProductModifiers,
        selectedIds
    ]);

    const prevFormProductModifiersRef = React.useRef<string>(JSON.stringify(formProductModifiers));

    // Sync ref when formProductModifiers prop changes externally
    React.useEffect(() => {
        prevFormProductModifiersRef.current = JSON.stringify(formProductModifiers);
    }, [formProductModifiers]);

    const handleChange = React.useCallback(
        (allFormValues: any) => {
            const currentFormProductModifiers = allFormValues.productModifiers;
            const currentState = JSON.stringify(currentFormProductModifiers);

            // Early return if there's no actual change from the previous state
            if (currentState === prevFormProductModifiersRef.current) {
                return;
            }

            const checkParentProductSelection = () => {
                let modified = false;
                const clonedFormProductModifiers = [...currentFormProductModifiers];

                for (let i = 0; i < clonedFormProductModifiers.length; i++) {
                    const formValue = clonedFormProductModifiers[i];
                    const modifier = allProductModifiers[i];

                    if (modifier?.parentModifierId && modifier?.parentProductId) {
                        const parentModifierIndex = allProductModifiers.findIndex(
                            m => m.id === modifier.parentModifierId
                        );

                        if (parentModifierIndex !== -1) {
                            const parentModifierFormValue = clonedFormProductModifiers[parentModifierIndex];

                            const isParentProductSelected = Array.isArray(parentModifierFormValue)
                                ? parentModifierFormValue.some(
                                      productId => productId === modifier.parentProductId
                                  )
                                : parentModifierFormValue === modifier.parentProductId;

                            if (!isParentProductSelected) {
                                clonedFormProductModifiers[i] = Array.isArray(formValue) ? [] : '';
                                modified = true;
                            }
                        }
                    }
                }

                return { clonedFormProductModifiers, modified };
            };

            const { modified, clonedFormProductModifiers } = checkParentProductSelection();
            if (modified) {
                const newState = JSON.stringify(clonedFormProductModifiers);
                if (newState !== currentState) {
                    prevFormProductModifiersRef.current = newState;
                    setFieldValue('productModifiers', clonedFormProductModifiers, false);
                }
            } else {
                prevFormProductModifiersRef.current = currentState;
            }
        },
        [allProductModifiers, setFieldValue]
    );

    if (!showModifier) {
        return null;
    }

    if (modifier.allowQuantitySelect && modifier.max > 1) {
        return (
            <Box sx={{ padding: 2 }} key={`modifier-${modifier.title}-${index}`}>
                <QuantityFormField
                    name={`productModifiers[${index}]`}
                    label={modifier.title}
                    labels={modifier.products.reduce<Record<string, QuantityLabel>>(
                        getProductQuantityModifierOptions(modifier.bundle),
                        {}
                    )}
                    collapsed={modifier.isCollapsed}
                    validate={modifierValidation(modifier)}
                    className={classes.modifierSelect}
                    max={modifier.max}
                    min={modifier.min}
                    onValueChange={handleChange}
                />
            </Box>
        );
    }
    const sortedModifierProducts = modifier.products.sort((a, b) => a.sort - b.sort);
    return (
        <StyledBox key={`modifier-${modifier.title}-${index}`}>
            <ModifierSelectFormField
                name={`productModifiers[${index}]`}
                label={modifier.title}
                options={sortedModifierProducts.map(renderProductModifierOptions(modifier.bundle))}
                validate={modifierValidation(modifier)}
                defaultValue={selectedIds}
                className={classes.modifierSelect}
                multiple={modifier.max > 1}
                collapsed={modifier.isCollapsed}
                description={renderModifierSelectionInfo(modifier.min, modifier.max)}
                maxSelection={modifier.max}
                required={modifier.min > 0}
                onValueChange={handleChange}
            />
        </StyledBox>
    );
}
